// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: "● Live · updated 8:50" after the Agents card's title in the Assistant chat (r4-rest), on
// the one Agents card that keeps updating in place (the server's live pick, else the newest).

import styles from '@app/features/konvo_assistant/components/chat_chrome/AssistantCardLiveLabel.module.css';
import {LIVE, LIVE_UPDATED} from '@app/features/konvo_assistant/components/chat_chrome/AssistantChatChromeMessages';
import {liveAgentsMessageId} from '@app/features/konvo_assistant/components/chat_chrome/AssistantChatChromeModel';
import AssistantChat from '@app/features/konvo_assistant/state/AssistantChat';
import type {Message} from '@app/features/messaging/models/MessagingMessage';
import {ChannelMessages} from '@app/features/messaging/state/ChannelMessages';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import * as DateUtils from '@app/features/user/utils/DateFormatting';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';

/** True when this message carries the live Agents card of its chat. */
export function useIsLiveAgentsCard(message: Message): boolean {
	const channelMessages = ChannelMessages.get(message.channelId);
	void channelMessages?.version;
	const messages = channelMessages?.toArray() ?? [];
	return liveAgentsMessageId(messages, AssistantChat.state) === message.id;
}

export const AssistantCardLiveLabel = observer(({message}: {message: Message}) => {
	const {i18n} = useLingui();
	const live = useIsLiveAgentsCard(message);
	if (!live) return null;
	const current = ChannelMessages.get(message.channelId)?.get(message.id) ?? message;
	const updatedAt = current.editedTimestamp ?? current.timestamp;
	return (
		<span className={styles.live} data-testid="assistant-live-label" data-flx="konvo-assistant.card-live-label">
			<span className={styles.dot} aria-hidden="true" />
			{/* The phone header has no room for the time: "updated 8:50" would be cut mid-word. */}
			{MobileLayout.isMobileLayout()
				? i18n._(LIVE)
				: i18n._(LIVE_UPDATED, {time: DateUtils.getFormattedTime(updatedAt)})}
		</span>
	);
});
